import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Eye, EyeOff, Factory, KeyRound, Mail, UsersRound } from "lucide-react";
import { isEmailLike } from "@hisense/shared";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { LanguageSwitch } from "@/components/shell/LanguageSwitch";
import { EnvironmentBanner } from "@/components/shell/EnvironmentBanner";
import { useAuth } from "@/app/auth";
import { ApiClientError } from "@/lib/api";

const DEV_DEMO_EMAIL = import.meta.env.DEV
  ? (import.meta.env.VITE_DEMO_EMAIL ?? "")
  : "";
const DEV_DEMO_PASSWORD = import.meta.env.DEV
  ? (import.meta.env.VITE_DEMO_PASSWORD ?? "")
  : "";
/**
 * HAI LỐI VÀO, MỘT ĐƯỜNG ĐĂNG NHẬP.
 *
 * Khách hỏi 15/09/2026: sao không tách nhân viên với nhà cung cấp? Tách phần
 * NHÌN THẤY thì đúng — một ô ghi "Email hoặc mã nhà cung cấp" với chỗ điền trộn
 * hai dạng là bắt người ngoài công ty tự đoán phần nào dành cho mình.
 *
 * Nhưng thẻ này KHÔNG GÁC gì hết: hai thẻ gửi đúng một yêu cầu tới đúng một
 * đường, nên chọn nhầm thẻ vẫn đăng nhập được. Nếu để thẻ quyết định loại danh
 * tính được nhận thì nhà cung cấp đứng nhầm thẻ sẽ nhận "sai tài khoản" trong
 * khi tài khoản của họ hoàn toàn đúng — đó là lời từ chối tệ nhất có thể nói
 * với một người ở ngoài công ty, không có ai để hỏi lại. Và hai đường đăng nhập
 * là hai chỗ để quy tắc phiên, khoá tài khoản và giới hạn nhập sai lệch nhau.
 */
type Audience = "staff" | "supplier";
const AUDIENCE_STORAGE_KEY = "hisense.login.audience";
const readAudience = (): Audience => {
  try {
    return localStorage.getItem(AUDIENCE_STORAGE_KEY) === "supplier" ? "supplier" : "staff";
  } catch {
    // Cửa sổ ẩn danh hoặc trình duyệt chặn lưu trữ. Đây chỉ là tiện ích nhớ
    // lựa chọn lần trước, không đáng để làm hỏng màn hình đăng nhập.
    return "staff";
  }
};

const HISENSE_LOGO_SRC = "/brand/hisense-logo.webp";
const LOGIN_SCULPTURE_POSTER = "/brand/login-button-sculpture-scene.webp";
const LOGIN_SCULPTURE_VIDEO = "/brand/login-button-sculpture-scene.mp4";

function useLoginArtwork() {
  const [artwork, setArtwork] = useState<"none" | "still" | "video">("none");

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    // Do not mount a hidden video on mobile: display:none still downloads media.
    const sync = () => setArtwork(!desktop.matches ? "none" : reduced.matches ? "still" : "video");
    sync();
    desktop.addEventListener("change", sync);
    reduced.addEventListener("change", sync);
    return () => {
      desktop.removeEventListener("change", sync);
      reduced.removeEventListener("change", sync);
    };
  }, []);

  return artwork;
}

function LoginSculpture({
  className,
  preload = "metadata",
  reduceMotion,
}: {
  className: string;
  preload?: "auto" | "metadata";
  reduceMotion: boolean;
}) {
  const media = reduceMotion ? (
    <img
      src={LOGIN_SCULPTURE_POSTER}
      alt=""
      width={960}
      height={898}
      className="login-sculpture-media"
    />
  ) : (
    <video
      className="login-sculpture-media"
      autoPlay
      muted
      loop
      playsInline
      preload={preload}
      poster={LOGIN_SCULPTURE_POSTER}
      width={1440}
      height={1440}
      disablePictureInPicture
    >
      <source src={LOGIN_SCULPTURE_VIDEO} type="video/mp4" />
    </video>
  );

  return (
    <div className={className} aria-hidden="true">
      {media}
    </div>
  );
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  /**
   * Nơi quay lại sau khi đăng nhập.
   *
   * RequireAuth đã ghi lại đường dẫn đang xem vào `state.from` từ trước, nhưng
   * không ai đọc — nên phiên hết hạn giữa lúc đang xem một PI là quay lại phải
   * tự tìm lại chứng từ đó.
   *
   * CHỈ nhận đường dẫn nội bộ: phải bắt đầu bằng đúng một dấu "/". "//evil.com"
   * và "/\evil.com" đều được trình duyệt hiểu là tên miền khác, nên chấp nhận
   * chúng là mở đường chuyển hướng ra ngoài từ một liên kết gửi qua email.
   */
  // `state` mất khi bị chuyển hướng cứng lúc phiên hết hạn (xem onSessionExpired),
  // nên chấp nhận cả query `?from=`.
  const rawFrom =
    (location.state as { from?: unknown } | null)?.from ??
    new URLSearchParams(location.search).get("from");
  const from =
    typeof rawFrom === "string" && /^\/(?![/\\])/.test(rawFrom) ? rawFrom : "/";
  const { user, login } = useAuth();
  const artwork = useLoginArtwork();
  const [email, setEmail] = useState(DEV_DEMO_EMAIL);
  const [password, setPassword] = useState(DEV_DEMO_PASSWORD);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [audience, setAudience] = useState<Audience>(readAudience);
  const [wrongTab, setWrongTab] = useState(false);
  const supplier = audience === "supplier";

  const pickAudience = (next: Audience) => {
    setAudience(next);
    setError(null);
    setWrongTab(false);
    try {
      localStorage.setItem(AUDIENCE_STORAGE_KEY, next);
    } catch {
      // Không lưu được thì lần sau mở lại về thẻ mặc định — chấp nhận được.
    }
  };

  useEffect(() => {
    if (!user) return;
    /**
     * Tài khoản NHÀ CUNG CẤP về thẳng cổng của họ, không về bảng điều khiển.
     *
     * Khách chốt 15/09/2026: nhà cung cấp tự đăng nhập ngay ở trang này. Họ
     * không có việc gì ở bảng điều khiển nội bộ — mọi truy vấn của họ đã bị
     * kẹp trong phạm vi nhà cung cấp của chính mình, nên màn hình đó chỉ hiện
     * một loạt ô trống và trông như phần mềm hỏng.
     */
    navigate(user.portalSupplierId ? "/portal" : from, { replace: true });
  }, [user, navigate, from]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setWrongTab(false);
    setBusy(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === "invalid_credentials") {
        setError(t("login.errorInvalidCredentials"));
        /*
         * Gợi ý "đứng nhầm thẻ" đoán từ HÌNH DẠNG chuỗi, ngay trên máy người
         * dùng — không hỏi máy chủ. Máy chủ vẫn trả đúng một câu từ chối chung
         * cho mọi trường hợp, nên không lộ tài khoản nào có thật.
         */
        const typed = email.trim();
        setWrongTab(typed !== "" && isEmailLike(typed) !== (audience === "staff"));
      } else if (err instanceof ApiClientError && err.code === "account_disabled") {
        setError(t("login.errorAccountDisabled"));
      } else {
        setError(t("login.errorGeneric"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-dvh bg-login-canvas text-ink">
      {/* Trên màn rộng phần đầu trang nằm tuyệt đối cách đỉnh 3.5rem, nên dải báo nằm đè lên khoảng trống
          đó thay vì đẩy cả trang dài thêm một dòng cuộn. */}
      <EnvironmentBanner className="relative z-40 lg:absolute lg:inset-x-0 lg:top-0" />
      <header className="relative z-30 flex items-center justify-between gap-4 px-5 py-5 sm:px-8 lg:absolute lg:inset-x-0 lg:top-14 lg:px-14 lg:py-0 lg:pr-20">
        <img
          src={HISENSE_LOGO_SRC}
          alt={t("login.brandLogoAlt")}
          width={640}
          height={167}
          className="h-auto w-32 shrink-0 sm:w-40 lg:w-60"
        />
        <LanguageSwitch variant="login" />
      </header>

      {artwork !== "none" && (
        <LoginSculpture
          reduceMotion={artwork === "still"}
          preload="auto"
          className="login-sculpture-stage pointer-events-none absolute bottom-0 left-0 z-0 hidden select-none lg:block"
        />
      )}

      <main className="relative z-20 ml-auto flex w-full items-start justify-center px-5 pb-8 pt-6 sm:px-8 sm:pt-10 lg:min-h-dvh lg:w-[38%] lg:items-center lg:justify-start lg:px-8 lg:pb-12 lg:pt-36 xl:w-[36%] xl:px-12 2xl:px-16">
        <form onSubmit={submit} className="w-full max-w-[26rem]">
          <p className="mb-7 hidden text-[0.75rem] font-semibold uppercase tracking-[0.09em] text-brand lg:block">
            {t("login.subtitle")}
          </p>
          <h1 className="text-[2rem] font-bold leading-tight tracking-[-0.02em] text-ink lg:text-[3.75rem] lg:leading-none">
            {t("login.title")}
          </h1>

          <div className="mt-6 lg:border-t lg:border-line-strong lg:pt-6">
            {/* Hai lối vào, một đường đăng nhập — xem ghi chú đầu tệp. */}
            <p id="login-audience-label" className="mb-2 text-[0.8125rem] font-medium text-ink-soft lg:mb-3">
              {t("login.audienceLabel")}
            </p>
            <div
              role="group"
              aria-labelledby="login-audience-label"
              className="grid grid-cols-2 gap-1 rounded-lg border border-line-strong bg-surface/35 p-1"
            >
              {([
                { id: "staff" as const, label: t("login.audienceStaff"), Icon: UsersRound },
                { id: "supplier" as const, label: t("login.audienceSupplier"), Icon: Factory },
              ]).map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={audience === id}
                  onClick={() => pickAudience(id)}
                  className={`relative flex min-h-12 min-w-0 items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-center text-[0.8125rem] font-semibold leading-snug transition-colors duration-150 focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none lg:min-h-20 lg:flex-col lg:gap-2 lg:py-3 ${
                    audience === id
                      ? "border-brand/30 bg-surface text-brand-ink shadow-sm"
                      : "border-transparent text-ink-soft hover:bg-surface/65 hover:text-ink"
                  }`}
                >
                  <Icon className="size-4 shrink-0 lg:size-5" aria-hidden="true" />
                  <span>{label}</span>
                  {audience === id && (
                    <span className="absolute right-1 top-1 grid size-3 place-items-center rounded-full bg-brand text-brand-on lg:right-2 lg:top-2 lg:size-4" aria-hidden="true">
                      <Check className="size-2.5 lg:size-3" strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              ))}
            </div>
            <p id="login-audience-description" aria-live="polite" aria-atomic="true" className="mt-3 min-h-10 text-[0.8125rem] leading-5 text-ink-soft">
              {supplier ? t("login.supplierHint") : t("login.staffHint")}
            </p>

            <div className="mt-5 space-y-5 lg:mt-6 lg:space-y-6">
              <div>
                <FieldLabel htmlFor="identifier" className="mb-2 text-[0.8125rem]">
                  {supplier ? t("login.supplierId") : t("login.email")}
                </FieldLabel>
                {/*
                  `type="text"`, KHÔNG phải `type="email"`.
                  Khách chốt 15/09/2026: nhà cung cấp đăng nhập bằng mã ta cấp,
                  không phải email. Để `type="email"` thì trình duyệt tự chặn
                  mọi chuỗi không có dấu @ — nhà cung cấp gõ đúng mã của mình
                  vẫn không bấm được nút, mà lời từ chối đến từ trình duyệt nên
                  không dịch được và không giải thích được.

                  Vẫn là `text` cả ở thẻ Nhân viên: thẻ chỉ đổi cách trình bày,
                  không đổi thứ được chấp nhận.
                */}
                <Input
                  id="identifier"
                  name="identifier"
                  type="text"
                  required
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  inputMode={supplier ? "text" : "email"}
                  aria-describedby="login-audience-description"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  leading={supplier ? <Factory /> : <Mail />}
                  placeholder={supplier ? t("login.supplierIdPlaceholder") : t("login.emailPlaceholder")}
                  className="h-13 bg-surface/85 px-3.5 text-[1rem] shadow-none lg:bg-surface/65 lg:text-[0.875rem]"
                />
              </div>
              <div>
                <FieldLabel htmlFor="password" className="mb-2 text-[0.8125rem]">
                  {t("login.password")}
                </FieldLabel>
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  leading={<KeyRound />}
                  trailing={
                    <button
                      type="button"
                      className="grid size-11 place-items-center rounded-xs text-ink-faint transition-colors hover:bg-surface hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand"
                      aria-label={t(
                        showPassword
                          ? "login.hidePassword"
                          : "login.showPassword",
                      )}
                      aria-pressed={showPassword}
                      onClick={() => setShowPassword((visible) => !visible)}
                    >
                      {showPassword ? (
                        <EyeOff className="size-4" aria-hidden="true" />
                      ) : (
                        <Eye className="size-4" aria-hidden="true" />
                      )}
                    </button>
                  }
                  placeholder="••••••••"
                  className="h-13 bg-surface/85 px-3.5 text-[1rem] shadow-none lg:bg-surface/65 lg:text-[0.875rem]"
                />
              </div>

              {/* Ô "Ghi nhớ đăng nhập" đã bị gỡ: nó không nối vào đâu cả —
                  bật hay tắt đều cho ra cùng một phiên, cùng một hạn. Một ô
                  đánh dấu không làm gì là lời hứa suông về bảo mật. Thời hạn
                  phiên do máy chủ quyết định (cookie httpOnly). */}

              {error && (
                <p
                  role="alert"
                  className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger"
                >
                  {error}
                </p>
              )}

              {/*
                Đứng nhầm thẻ vẫn ĐĂNG NHẬP ĐƯỢC, nên dòng này chỉ hiện khi đã
                thật sự bị từ chối — và nó nói về thẻ, không nói về tài khoản.
              */}
              {wrongTab && (
                <p role="status" className="rounded-sm bg-warn-tint px-3 py-2 text-xs text-warn">
                  {supplier ? t("login.wrongTabSupplier") : t("login.wrongTabStaff")}
                </p>
              )}

              <Button
                type="submit"
                size="lg"
                className="h-13 w-full text-[1rem] shadow-none lg:mt-2 lg:h-14 lg:text-[1.0625rem]"
                disabled={busy}
              >
                {busy ? t("login.submitting") : t("login.submit")}
                {!busy && <ArrowRight className="size-5" />}
              </Button>

            </div>

            {import.meta.env.DEV && DEV_DEMO_EMAIL && DEV_DEMO_PASSWORD && (
              <p className="mt-6 text-center text-xs text-ink-faint">
                {t("login.demoLabel")}: {" "}
                <span className="font-mono">{DEV_DEMO_EMAIL}</span> ·{" "}
                <span className="font-mono">{DEV_DEMO_PASSWORD}</span>
              </p>
            )}
          </div>

          <div className="mt-8 hidden border-t border-line-strong lg:block" aria-hidden="true" />
        </form>
      </main>
    </div>
  );
}
